import type { OnboardingMode } from '../stores/onboarding';

/**
 * Does entering suite setup ask the FORK (guide me vs start empty), or go straight
 * into the wizard?
 *
 * ADR 0032 amendment (2026-09-01), "the fork is asked from demo too". The rule it
 * states is one sentence — **the fork is re-asked exactly when there is no staged
 * work to lose** — and this is the only place that sentence is written as code:
 *
 * - From a live DEMO with nothing staged, setup asks the fork. A user exploring the
 *   demo who decides to get started must be able to say "just clear this out"
 *   instead of being handed the guided wizard with no way out of it.
 * - From a live DEMO with a non-empty staged blob, it does NOT. That blob is the
 *   record of an earlier "guide me", so re-asking would put the choice in front of
 *   someone part-way through answering it — and a "start empty" answer there would
 *   silently discard their work (`commit/begin { empty: true }` drops the blob).
 * - From `setup` it does not either: on a fresh tenant the MODE is the record of the
 *   choice (`choose-setup` wrote it), so nothing needs re-asking. The amendment's
 *   asymmetry is specifically about demo, where the mode cannot record the choice —
 *   the demo has to stay `demo` for Finish's wipe to have anything to wipe.
 * - A non-admin never resolves the fork, and a resolved tenant has no setup to enter.
 *
 * `hasStagedWork` is read off the SERVER (`status.staged`, the master row's
 * `stagedConfigJson` minus its reserved keys), never off a client-side "they already
 * picked guided" flag: such a flag forgets on reload and in another browser, which is
 * exactly the case the amendment is protecting.
 *
 * Kept as a pure function, apart from both callers, because the rule has two
 * enforcement points — the router guard (which every entry passes: the demo banner,
 * a module UI's deep link to `<workspace>/?setup=1`, `/setup`, a bookmark, a
 * restored tab) and the workspace demo banner's own CTA (which routes straight
 * there to avoid a visible bounce). Two copies of an asymmetric rule is how the two
 * halves drift apart.
 */
export function asksForkOnSetupEntry(status: {
  mode: OnboardingMode;
  isAdmin: boolean;
  firstRunResolved: boolean;
  hasStagedWork: boolean;
}): boolean {
  if (!status.isAdmin) return false;
  if (status.firstRunResolved) return false;
  if (status.mode !== 'demo') return false;
  return !status.hasStagedWork;
}
